import {
  AgentMessage,
  ProfileName,
  SnapshotChannel,
  describeToolCall,
  getEventText,
  getText,
  getToolCalls,
  hiddenTurnIndexes,
  indexToolResults,
  isOpeningMessage,
  splitReplyOptions,
  stripMarkdown
} from "./protocol";

export interface SessionLogEntry {
  kind: "user" | "assistant" | "event" | "action";
  text: string;
}

export interface PostIdentity {
  id?: string | null;
  groupId?: string | null;
  isDraft?: boolean;
  isPostGroup?: boolean;
}

export interface SavedPostResponse {
  posts?: { id?: string; groupId?: string | null }[];
  post?: { id?: string };
  id?: string;
  isDraft?: boolean;
}

export type LogChannel = Pick<SnapshotChannel, "id" | "name">;

export interface LogNames {
  channels: LogChannel[];
  profiles?: ProfileName[];
}

const MAX_RECENT_ACTIONS = 4;

export const getPostKey = (post: PostIdentity | null | undefined): string | null => {
  if (!post) return null;
  if (post.isPostGroup) {
    const groupId = post.groupId ?? post.id;
    return groupId ? `group:${groupId}` : null;
  }
  if (!post.id) return null;
  return `${post.isDraft ? "draft" : "post"}:${post.id}`;
};

export const getSavedPostKey = (response: SavedPostResponse | null | undefined): string | null => {
  if (!response) return null;
  const posts = Array.isArray(response.posts) ? response.posts : [];
  if (posts.length > 1 && posts[0].groupId) return `group:${posts[0].groupId}`;
  const post = posts[0] ?? response.post ?? response;
  if (!post?.id) return null;
  return `${response.isDraft ? "draft" : "post"}:${post.id}`;
};

export const buildSessionLog = (history: AgentMessage[], { channels, profiles = [] }: LogNames): SessionLogEntry[] => {
  const results = indexToolResults(history);
  const hidden = hiddenTurnIndexes(history);
  return history.flatMap((message, index): SessionLogEntry[] => {
    if (hidden.has(index)) return [];
    // The greeting answers the opening, so it would read as work done when the session is replayed.
    if (message.role === "assistant" && index > 0 && isOpeningMessage(history[index - 1].content)) return [];
    if (message.role === "user") {
      const event = getEventText(message.content);
      if (event !== null) return [{ kind: "event", text: event }];
      const text = getText(message.content);
      return text ? [{ kind: "user", text }] : [];
    }
    if (message.role !== "assistant") return [];
    const { text } = splitReplyOptions(stripMarkdown(getText(message.content)));
    const actions = getToolCalls(message.content).map((call): SessionLogEntry => {
      const result = results[call.id];
      if (result?.isError) return { kind: "action", text: `Failed: ${result.output}` };
      const taggedNames = result?.tagged?.length ? ` (${result.tagged.map(person => person.name).join(", ")})` : "";
      return { kind: "action", text: `${describeToolCall(call, channels, profiles)}${taggedNames}` };
    });
    return text ? [{ kind: "assistant", text }, ...actions] : actions;
  });
};

export const mergeSessionLog = (previous: SessionLogEntry[], history: AgentMessage[], names: LogNames): SessionLogEntry[] => [
  ...previous,
  ...buildSessionLog(history, names)
];

const countLabel = (count: number): string => (count === 2 ? "twice" : `${count} times`);

// Repeated actions collapse into one entry with a count, ordered by their latest occurrence.
export const listRecentActions = (log: SessionLogEntry[], max: number = MAX_RECENT_ACTIONS): string[] => {
  const counts = new Map<string, number>();
  log
    .filter(entry => entry.kind === "action" && !entry.text.startsWith("Failed:"))
    .forEach(entry => {
      const count = (counts.get(entry.text) ?? 0) + 1;
      counts.delete(entry.text);
      counts.set(entry.text, count);
    });
  return Array.from(counts.entries())
    .slice(-max)
    .map(([text, count]) => (count > 1 ? `${text} ${countLabel(count)}` : text));
};
